<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <ProgressNavComponent
      v-if="signupStep > 1 && !changingInformationDisabled"
      :steps="navSteps"
      :setStep="setStep"
    />

    <!-- STEP 1: Country selection and onboarding type -->
    <div v-if="shownStep.step_name === StepName.CHOOSE_ONBOARDING">
      <CountryPickerComponent />

      <ChooseOnboardingComponent
        :addAuthorizationAgreementFlow="createAuthorizationAccountFlow"
        :addNewDeviceFlow="createNewDeviceFlow"
        :existingCompanies="existingCompanies"
        :countryLocale="'se'"
        :enableEecsConversionFlow="true"
        :addEecsConversionFlow="handleConversionFlow"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
      />
    </div>

    <!-- STEP 2: Contact review (AUTH Step 2) -->
    <div
      v-if="shownStep.step_name === StepName.NEW_AUTHORIZATION_CONTACT_REVIEW"
    >
      <NewAuthorizationContactReviewConfigurationComponent
        v-if="companyIndex !== -1"
        :key="companyIndex"
        :accountData="accountData"
        :companyIndex="companyIndex"
        @handleUpdateCompany="signupStore.updateCompany"
        @updateAccountData="signupStore.updateAccount"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
        class="mb-4"
      />
    </div>

    <!-- STEP 2: Contact review (NEW ACCOUNT/DEVICE Step 2) -->
    <div v-if="shownStep.step_name === StepName.NEW_ACCOUNT_CONTACT_REVIEW">
      <NewAccountContactReviewConfigurationComponent
        v-if="companyIndex !== -1"
        :key="companyIndex"
        :accountData="accountData"
        :companyIndex="companyIndex"
        @handleUpdateCompany="signupStore.updateCompany"
        @updateAccountData="signupStore.updateAccount"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
        class="mb-4"
      />
      <AddressReviewComponent
        v-if="generatorIndex !== -1"
        :account-data="accountData"
        :generator-index="generatorIndex"
        :company-index="companyIndex"
        @handleUpdateGenerator="handleUpdateGenerator"
      />
    </div>

    <!-- NEW ACCOUNT/DEVICE Step 3: Generator review -->
    <GeneratorReviewComponent
      v-if="
        shownStep.step_name === StepName.GENERATOR_REVIEW &&
        generatorIndex !== -1
      "
      :account-data="accountData"
      :generator-index="generatorIndex"
      :company-index="companyIndex"
      :conversionFromNationalToEECS="effectiveEecsConversion"
      @handleUpdateGenerator="handleUpdateGenerator"
    />

    <!-- NEW ACCOUNT/DEVICE Step 4: Grid review -->
    <GridReviewComponent
      v-if="
        shownStep.step_name === StepName.GRID_REVIEW && generatorIndex !== -1
      "
      :account-data="accountData"
      :generator-index="generatorIndex"
      :company-index="companyIndex"
      @handleUpdateGenerator="handleUpdateGenerator"
    />

    <!-- AUTH Step 3 / NEW ACCOUNT/DEVICE Step 5: Contract review -->
    <SEContractReviewComponent
      v-if="
        shownStep.step_name === StepName.CONTRACT_REVIEW && companyIndex !== -1
      "
      :accountData="accountData"
      :addNewDevice="!addAuthorizationAgreement"
      :conversionFromNationalToEECS="effectiveEecsConversion"
      @createContainerForStartSigningReady="storeCreateContainerForStartSigning"
      @disableChangingInformation="handleDisableChangingInformation"
      class="mb-4"
    />

    <!-- Buttons -->
    <div
      v-if="signupStep > 1 && !changingInformationDisabled"
      class="d-flex justify-content-between w-100"
    >
      <button
        class="btn btn-secondary me-2"
        :disabled="isLoading"
        @click="setStep(signupStep - 1)"
      >
        {{ $t("signup.navsteps.back") }}
      </button>

      <button
        class="btn btn-primary"
        :disabled="isLoading"
        @click="
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? createContainerForStartSigningFunction()
            : setStep(signupStep + 1)
        "
      >
        {{
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? $t("signup.navsteps.to_signature_page")
            : $t("signup.navsteps.next")
        }}
      </button>
    </div>
    <!-- Loading overlay, shown only when isLoading is true -->
    <LoadingOverlayComponent v-if="isLoading" />
    <!-- AccountDataDebugComponent for debugging -->
    <AccountDataDebugComponent
      :accountData="accountData"
      class="mb-4"
    ></AccountDataDebugComponent>
  </div>
</template>

<script setup lang="ts">
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import { useQueryManager } from "@/composables/useQueryManager";
import { StepConfig, useStepFunctions } from "@/composables/useStepFunctions";
import ChooseOnboardingComponent from "@/signup/ChooseOnboardingComponent.vue";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import ProgressNavComponent from "@/signup/ProgressNavComponent.vue";
import { useSignupStepSave } from "@/signup/composables/useSignupStepSave";
import { useSignupStore } from "@/signup/signup";
import { storeToRefs } from "pinia";
import type { Company, Generator } from "@/types";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import AddressReviewComponent from "@/signup/se/AddressReviewComponent.vue";
import { FlowType, RegistrationStatus } from "@/signup/se/enums";
import GeneratorReviewComponent from "@/signup/se/GeneratorReviewComponent.vue";
import NewAccountContactReviewConfigurationComponent from "@/signup/se/NewAccountContactReviewConfigurationComponent.vue";
import NewAuthorizationContactReviewConfigurationComponent from "@/signup/se/NewAuthorizationContactReviewConfigurationComponent.vue";
import SEContractReviewComponent from "@/signup/se/SEContractReviewComponent.vue";
import GridReviewComponent from "./GridReviewComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

interface SeFullCompanySeAddress {
  postal_address: string;
  postal_code: string;
  city: string;
}

enum StepName {
  CHOOSE_ONBOARDING,
  NEW_AUTHORIZATION_CONTACT_REVIEW,
  NEW_ACCOUNT_CONTACT_REVIEW,
  GENERATOR_REVIEW,
  GRID_REVIEW,
  CONTRACT_REVIEW,
}

const formatFullAddress = (address: SeFullCompanySeAddress): string => {
  return `${address.postal_address}, ${address.postal_code}, ${address.city}`;
};

const createContainerForStartSigningFunction = ref<() => void | Promise<void>>(
  () => undefined
);

const isLoading = ref(true);
const { globalErrorHandler } = useGlobalHandlers();
const signupStore = useSignupStore();
const {
  account: accountData,
  companyIndex,
  generatorIndex,
} = storeToRefs(signupStore);
const { updateQuery, getQueryValue, parsers } = useQueryManager();
const route = useRoute();
const signupStep = getQueryValue("step", (value) =>
  value ? parseInt(value, 10) : 1
);
const addAuthorizationAgreement = getQueryValue(
  "addAuthorizationAgreement",
  parsers.boolean
);
const eecsConversion = getQueryValue("eecs_conversion", parsers.boolean);

// Compute effective EECS conversion based on URL param or persisted flow_type
const effectiveEecsConversion = computed(() => {
  // If eecs_conversion query param is explicitly present (true or false), respect that choice
  if (route.query.eecs_conversion !== undefined) {
    return eecsConversion.value;
  }

  // Only fall back to persisted state if no explicit query param
  if (generatorIndex.value !== -1) {
    const generator =
      accountData.value.companies[companyIndex.value].generators[
        generatorIndex.value
      ];

    // Only use flow_type for inference - registration_status is ambiguous
    if (generator?.application_information?.flow_type === FlowType.CONVERSION) {
      return true;
    }
    if (generator?.application_information?.flow_type === FlowType.NEW_DEVICE) {
      return false;
    }
  }

  return false;
});

const { postStepCallback } = useSignupStepSave(isLoading);
const { t } = useI18n();

const existingCompanies = computed(() => {
  return accountData.value.companies.filter(
    (company) =>
      company.id !== undefined &&
      company.country === "SE" &&
      company.documents.length > 0
  );
});

const hasGenerator = computed(() => {
  return generatorIndex.value !== -1;
});

const STEP_CONFIG: Record<StepName, StepConfig> = {
  [StepName.CHOOSE_ONBOARDING]: {
    step_name: StepName.CHOOSE_ONBOARDING,
    title: t("signup.navsteps.se.step1"),
    titleKey: "signup.navsteps.se.step1",
  },
  [StepName.NEW_AUTHORIZATION_CONTACT_REVIEW]: {
    step_name: StepName.NEW_AUTHORIZATION_CONTACT_REVIEW,
    title: t("signup.navsteps.se.authorization.contact_review"),
    titleKey: "signup.navsteps.se.authorization.contact_review",
    apiEndpoint: "/api/se/update-company",
    postStepCallback: postStepCallback,
    preStepCallback: async (_: StepConfig) => {
      signupStore.createCompanyIfMissing();
      return true;
    },
  },
  [StepName.NEW_ACCOUNT_CONTACT_REVIEW]: {
    step_name: StepName.NEW_ACCOUNT_CONTACT_REVIEW,
    title: t("signup.navsteps.se.new_device.contact_review"),
    titleKey: "signup.navsteps.se.new_device.contact_review",
    apiEndpoint: "/api/se/update-generator",
    postStepCallback: postStepCallback,
    preStepCallback: async (_: StepConfig) => {
      signupStore.createCompanyIfMissing();
      if (!hasGenerator.value) {
        signupStore.createGeneratorIfMissing();
      }
      return true;
    },
  },
  [StepName.GENERATOR_REVIEW]: {
    step_name: StepName.GENERATOR_REVIEW,
    title: t("signup.navsteps.se.new_device.generator_review"),
    titleKey: "signup.navsteps.se.new_device.generator_review",
    apiEndpoint: "/api/se/update-generator",
    postStepCallback: postStepCallback,
    preStepCallback: async (_: StepConfig) => {
      if (!hasGenerator.value) {
        signupStore.createGeneratorIfMissing();
      }
      return true;
    },
  },
  [StepName.GRID_REVIEW]: {
    step_name: StepName.GRID_REVIEW,
    title: t("signup.navsteps.se.new_device.grid_review"),
    titleKey: "signup.navsteps.se.new_device.grid_review",
    apiEndpoint: "/api/se/update-generator",
    postStepCallback: postStepCallback,
    preStepCallback: async (_: StepConfig) => {
      if (!hasGenerator.value) {
        signupStore.createGeneratorIfMissing();
      }
      return true;
    },
  },
  [StepName.CONTRACT_REVIEW]: {
    step_name: StepName.CONTRACT_REVIEW,
    title: t("signup.navsteps.se.contract_review"),
    titleKey: "signup.navsteps.se.contract_review",
    postStepCallback: postStepCallback,
  },
};

const flowSteps = computed(() => {
  if (addAuthorizationAgreement.value) {
    return [
      STEP_CONFIG[StepName.CHOOSE_ONBOARDING],
      STEP_CONFIG[StepName.NEW_AUTHORIZATION_CONTACT_REVIEW],
      STEP_CONFIG[StepName.CONTRACT_REVIEW],
    ];
  } else if (effectiveEecsConversion.value) {
    return [
      STEP_CONFIG[StepName.CHOOSE_ONBOARDING],
      STEP_CONFIG[StepName.NEW_ACCOUNT_CONTACT_REVIEW],
      STEP_CONFIG[StepName.GENERATOR_REVIEW],
      STEP_CONFIG[StepName.CONTRACT_REVIEW],
    ];
  } else {
    return [
      STEP_CONFIG[StepName.CHOOSE_ONBOARDING],
      STEP_CONFIG[StepName.NEW_ACCOUNT_CONTACT_REVIEW],
      STEP_CONFIG[StepName.GENERATOR_REVIEW],
      STEP_CONFIG[StepName.GRID_REVIEW],
      STEP_CONFIG[StepName.CONTRACT_REVIEW],
    ];
  }
});

const { shownStep, setStep, navSteps, invokePreStepCallback } =
  useStepFunctions(flowSteps, signupStep);

const storeCreateContainerForStartSigning = (
  createContainerForStartSigning: () => Promise<void>
) => {
  createContainerForStartSigningFunction.value = createContainerForStartSigning;
};

const changingInformationDisabled = ref(false);

const handleDisableChangingInformation = () => {
  changingInformationDisabled.value = true;
};

const applyFlowSelection = async (options: {
  addAuthorizationAgreement: boolean;
  eecsConversion: boolean;
  flowType?: FlowType.NEW_DEVICE | FlowType.CONVERSION;
  registrationStatus?: RegistrationStatus;
}) => {
  isLoading.value = true;
  try {
    await updateQuery({
      addAuthorizationAgreement: options.addAuthorizationAgreement
        ? "true"
        : "false",
      eecs_conversion: options.eecsConversion ? "true" : "false",
    });

    if (generatorIndex.value !== -1 && options.flowType) {
      const generator =
        accountData.value.companies[companyIndex.value].generators[
          generatorIndex.value
        ];
      const appInfo = generator.application_information ?? {};
      const goApp = appInfo.go_application ?? {};
      const updatedGenerator = {
        ...generator,
        application_information: {
          ...appInfo,
          flow_type: options.flowType,
          go_application: {
            ...goApp,
            registration_status: options.registrationStatus,
          },
        },
      };
      handleUpdateGenerator(updatedGenerator);
    }

    setStep(2);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const createAuthorizationAccountFlow = async () => {
  await applyFlowSelection({
    addAuthorizationAgreement: true,
    eecsConversion: false,
  });
};

const createNewDeviceFlow = async () => {
  await applyFlowSelection({
    addAuthorizationAgreement: false,
    eecsConversion: false,
    flowType: FlowType.NEW_DEVICE,
    registrationStatus: RegistrationStatus.NOT_REGISTERED,
  });
};

const handleConversionFlow = async () => {
  await applyFlowSelection({
    addAuthorizationAgreement: false,
    eecsConversion: true,
    flowType: FlowType.CONVERSION,
    registrationStatus: RegistrationStatus.REGISTERED,
  });
};

const handleSelectExistingCompany = async (companyId: number) => {
  isLoading.value = true;
  try {
    await updateQuery({
      company_id: companyId.toString(),
    });
    setStep(2);
    signupStore.removeEmptyCompanyIfExists();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const handleResetCompanySelection = () => {
  updateQuery({
    company_id: undefined,
    generator_id: undefined,
  });
  signupStore.removeEmptyCompanyIfExists();
};

const handleUpdateGenerator = (event: Generator) => {
  const currentCompany = accountData.value.companies[companyIndex.value];
  const updatedValue = {
    ...currentCompany,
    // WORKAROUND: Only update address if full_company_address exists to prevent null dereference
    // when updating generator flow_type from step 1 before contact details are filled
    ...(event.application_information?.full_company_address && {
      address: formatFullAddress(
        event.application_information.full_company_address
      ),
    }),
  } as Company;
  signupStore.updateCompany(updatedValue);
  signupStore.updateGenerator(event);
};

onMounted(async () => {
  await invokePreStepCallback();
  isLoading.value = false;
});
</script>
